import {
  AddCircleOutline as AddCircleOutlineIcon,
  CloudUpload,
  Download,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { GET_DOWNLOAD_URL_MUTATION, PUBLISH_GIS_FILE_MUTATION } from '../../operations';
import React, { useState } from 'react';
import { formatTimestamp, truncate } from '../../../../utils/helpers';

import { AdminsPageShell } from '../../../../components/AdminsPageShell';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { GisFileModal } from '../GisFileModal';
import { styles } from './styles';
import { useGetGisFiles } from '../../hooks/useGetGisFiles';
import { useMutation } from '@apollo/client';

const LoadingState = () => {
  return (
    <Box sx={styles.loadingContainer}>
      <CircularProgress size={40} sx={styles.spinner} />
      <Typography sx={styles.loadingText}>Loading...</Typography>
    </Box>
  );
};

const ErrorState = ({ error, onRetry }) => {
  return (
    <Box sx={styles.errorContainer}>
      <ErrorOutlineIcon sx={styles.errorIcon} />
      <Typography sx={styles.errorText}>Something went wrong</Typography>
      <Typography sx={styles.errorMessage}>{error.message}</Typography>
      <Button variant="contained" color="primary" onClick={onRetry} sx={styles.retryButton}>
        Try Again
      </Button>
    </Box>
  );
};

export const GisFilesList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { files = [], loading, error, refetch } = useGetGisFiles();
  const [publishGisFile, { loading: publishing }] = useMutation(PUBLISH_GIS_FILE_MUTATION);
  const [getDownloadUrl, { loading: downloading }] = useMutation(GET_DOWNLOAD_URL_MUTATION);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSuccess = () => {
    refetch();
    handleCloseModal();
  };

  const handlePublish = async (id) => {
    try {
      await publishGisFile({
        variables: { id },
      });
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await getDownloadUrl({
        variables: { id },
      });

      if (response.data.getGisFileDownloadUrl.errors?.length) {
        throw new Error(response.data.getGisFileDownloadUrl.errors[0]);
      }

      // Open the download URL in a new tab
      window.open(response.data.getGisFileDownloadUrl.url, '_blank');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminsPageShell
      title="GIS Files"
      actions={
        <Button
          sx={styles.button}
          variant="contained"
          color="secondary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleOpenModal}
          disabled={publishing}
        >
          Upload File
        </Button>
      }
    >
      <Box sx={styles.container}>
        {error && <ErrorState error={error} onRetry={() => refetch()} />}
        {loading ? (
          <LoadingState />
        ) : files.length === 0 ? (
          <Box sx={styles.noFilesContainer}>
            <ErrorState error={{ message: 'No files found.' }} onRetry={() => refetch()} />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>Name</TableCell>
                  <TableCell sx={styles.tableHeader}>Description</TableCell>
                  <TableCell sx={styles.tableHeader}>Country/Region</TableCell>
                  <TableCell sx={styles.tableHeader}>Status</TableCell>
                  <TableCell sx={styles.tableHeader}>Created By</TableCell>
                  <TableCell sx={styles.tableHeader}>Published At</TableCell>
                  <TableCell sx={styles.tableHeader}>Published By</TableCell>
                  <TableCell sx={styles.tableHeader}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files?.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>{file.name}</TableCell>
                    <TableCell>{truncate(file.description, 30)}</TableCell>
                    <TableCell>{file.country ? file.country?.name : file.region?.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={file.isPublished ? 'Published' : 'Unpublished'}
                        sx={{
                          ...styles.chip,
                          ...(file.isPublished ? styles.published : styles.unpublished),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {file.createdBy
                        ? `${file.createdBy.firstName} ${file.createdBy.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {file.publishedAt ? formatTimestamp(file.publishedAt) : '-'}
                    </TableCell>
                    <TableCell>
                      {file.publishedBy
                        ? `${file.publishedBy.firstName} ${file.publishedBy.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={styles.actionButtons}>
                        <Tooltip title={file.isPublished ? 'Already published' : 'Publish'}>
                          <span>
                            <IconButton
                              color="secondary"
                              size="small"
                              disabled={file.isPublished || publishing}
                              onClick={() => handlePublish(file.id)}
                            >
                              {publishing ? <CircularProgress size={20} /> : <CloudUpload />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Download">
                          <span>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => handleDownload(file.id)}
                              disabled={downloading}
                            >
                              {downloading ? <CircularProgress size={20} /> : <Download />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <GisFileModal open={isModalOpen} onClose={handleCloseModal} onSuccess={handleSuccess} />
    </AdminsPageShell>
  );
};
